import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getRankerDanjiDetail,
  getRankerFixArticles,
  getRealTransactions,
  getRealTransactionsMarket,
  getAskPriceVolumeChart,
  getWeeklyListingVolume,
} from '../../lib/api/ranker-api';
import RealTransactionNewCard from '../RealTransactionNewCard';
import RankingDetailComplexInfoTable from './detail/RankingDetailComplexInfoTable';
import './RankingComplexDetailModal.css';
import './RankingComplexDetailModalV2.css';

const TRANSACTION_PAGE_SIZE = 20;

const HEATING_SYSTEM_TYPE_MAP = {
  HT001: '개별난방',
  HT002: '중앙난방',
  HT003: '개별열원',
  HT004: '중앙열원',
  HT005: '지역난방',
  HT006: '지역열원',
  HT000: '미노출',
};

const HEATING_ENERGY_TYPE_MAP = {
  HF001: '도시가스',
  HF002: '열병합',
  HF003: '기름',
  HF004: '전기',
  HF005: '심야전기',
  HF006: '태양열',
  HF007: 'LPG',
  HF008: '지열',
  HF000: '미노출',
};

const formatUseApproveDate = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const raw = String(value).trim();
  const digits = raw.replace(/\D/g, '');

  if (digits.length >= 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  if (digits.length >= 6) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}`;
  }
  return raw;
};

const fmt = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') return '-';
  return `${value}${suffix}`;
};

const fmtNumber = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return `${value}${suffix}`;
  return `${num.toLocaleString('ko-KR')}${suffix}`;
};

const fmtHeat = (value, type = 'system') => {
  if (!value) return '-';
  const raw = String(value).trim();
  const map = type === 'energy' ? HEATING_ENERGY_TYPE_MAP : HEATING_SYSTEM_TYPE_MAP;
  return map[raw] || raw;
};

const formatGradeBadgeText = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-등급';
  return num === 0 ? '-등급' : `${num}등급`;
};

const formatHeaderMeta = (detail, summary) => {
  const address = [
    detail?.sidoName,
    detail?.sigunguName,
    detail?.bjdName,
  ].filter(Boolean).join(' ');
  const addressText = `${address}${detail?.addressBunji ? ` ${detail.addressBunji}` : ''}`.trim() || '-';

  const useApproveDateText = formatUseApproveDate(detail?.useApproveDate);
  const useApproveDateShort = (() => {
    const raw = String(useApproveDateText || '').trim();
    const m = raw.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
    if (!m) return '-';
    const yyyy = m[1];
    const mm = m[2];
    return `${yyyy}.${mm}`;
  })();
  const useApproveAgeText = (() => {
    const yearMatch = String(useApproveDateText || '').match(/^(\d{4})/);
    const approvedYear = yearMatch ? Number(yearMatch[1]) : Number.NaN;
    const currentYear = new Date().getFullYear();
    const age = Number.isFinite(approvedYear) && approvedYear > 0
      ? Math.max(1, currentYear - approvedYear + 1)
      : Number.NaN;
    return Number.isFinite(age) ? `(${age}년차)` : '';
  })();

  const familyRaw = detail?.familyCnt ?? summary?.familyCnt ?? summary?.households ?? summary?.householdCount ?? null;
  const familyNum = Number(familyRaw);
  const familyText = Number.isFinite(familyNum) && familyNum > 0
    ? `${familyNum.toLocaleString('ko-KR')}세대`
    : '-세대';

  const rentalRaw = detail?.rentalCnt
    ?? detail?.rentalHouseholdCnt
    ?? detail?.leaseCnt
    ?? summary?.rentalCnt
    ?? summary?.rentalHouseholdCnt
    ?? summary?.leaseCnt
    ?? null;
  const rentalNum = Number(rentalRaw);
  const rentalText = Number.isFinite(rentalNum) && rentalNum > 0
    ? `${rentalNum.toLocaleString('ko-KR')}세대`
    : '-세대';

  return `${addressText} • ${useApproveDateShort}${useApproveAgeText} • ${familyText}(임대${rentalText})`;
};

const formatAreaOptionLabel = (option) => {
  const areaUnit = String(option?.__areaUnit || 'm2');

  if (areaUnit === 'pyeong') {
    const planTypeName2 = String(option?.planTypeName2 ?? option?.plan_type_name2 ?? '').trim();
    if (planTypeName2) return planTypeName2;
  }

  const splySpc = Number(option?.splySpc ?? option?.sply_spc ?? 0);
  if (Number.isFinite(splySpc) && splySpc > 0) {
    return `${splySpc % 1 === 0 ? splySpc.toFixed(0) : splySpc.toFixed(2)}㎡`;
  }

  if (areaUnit === 'pyeong') {
    const pyeong = Number(option?.supplyPyeong ?? option?.exclusivePyeong ?? option?.pyeong ?? 0);
    if (Number.isFinite(pyeong) && pyeong > 0) return `${Math.round(pyeong)}평`;
  }

  const fallbackSource = option?.label ?? option?.planTypeName ?? option?.plan_type_name ?? option?.ptpName ?? '';
  const fallback = String(fallbackSource).trim();
  return fallback || '평형';
};

const extractPyeongValue = (option) => {
  const planTypeName2 = String(option?.planTypeName2 ?? option?.plan_type_name2 ?? '').trim();
  const match = planTypeName2.match(/(\d+)/);
  if (match) return Number(match[1]);

  const pyeong = Number(option?.supplyPyeong ?? option?.exclusivePyeong ?? option?.pyeong ?? 0);
  if (Number.isFinite(pyeong) && pyeong > 0) return Math.round(pyeong);

  return null;
};

const normalizePtpToken = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const numeric = raw.replace(/[^\d.]/g, '');
  if (numeric) {
    const num = Number(numeric);
    if (Number.isFinite(num)) return String(num);
  }
  return raw;
};

const formatFixArticlePrice = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '-';

  const eokMatch = raw.match(/(\d+(?:\.\d+)?)\s*억/);
  if (eokMatch) return `${Number(eokMatch[1]).toLocaleString('ko-KR')}억`;

  const numeric = Number(raw.replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(numeric) || numeric <= 0) return raw;

  const eok = Math.floor(numeric / 10000);
  if (eok > 0) return `${eok.toLocaleString('ko-KR')}억`;
  return `${numeric.toLocaleString('ko-KR')}만원`;
};

function RankingComplexDetailModalV2({
  naverDanjiCode,
  summary,
  onClose,
  onToggleFavorite,
  isFavorite = false,
  onOpenFavoriteGroupPicker,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState('');
  const [txPeriodYears, setTxPeriodYears] = useState(null); // null = 기간 제한 없이 전체
  const [txMonthlyStats, setTxMonthlyStats] = useState([]);
  const [txChartItems, setTxChartItems] = useState([]);
  const [txMarketItems, setTxMarketItems] = useState([]);
  const [txTargetPrice, setTxTargetPrice] = useState(null);
  const [txMarketLoading, setTxMarketLoading] = useState(false);
  const [txPtpNo, setTxPtpNo] = useState('');
  const [selectedPyeong, setSelectedPyeong] = useState('');
  const [areaUnitMode, setAreaUnitMode] = useState('pyeong');
  const [areaExpanded, setAreaExpanded] = useState(false);
  const [txDealViewMode, setTxDealViewMode] = useState('sale');
  const [askPriceViewMode, setAskPriceViewMode] = useState('sale');
  const [txLoaded, setTxLoaded] = useState(false);
  const [txPageNumber, setTxPageNumber] = useState(1);
  const [txHasMoreRows, setTxHasMoreRows] = useState(false);
  const [txRowsLoadingMore, setTxRowsLoadingMore] = useState(false);
  const [txVisibleRowCount, setTxVisibleRowCount] = useState(TRANSACTION_PAGE_SIZE);
  const [weeklyRows, setWeeklyRows] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [fixArticles, setFixArticles] = useState([]);
  const [fixArticlesByType, setFixArticlesByType] = useState({ SALE: [], LEASE: [] });
  const [fixArticleTradeType, setFixArticleTradeType] = useState('SALE');
  const [fixArticlesLoading, setFixArticlesLoading] = useState(false);
  const [fixArticlesError, setFixArticlesError] = useState('');
  const [fixArticlesExpanded, setFixArticlesExpanded] = useState(false);
  const weeklyRequestSeqRef = useRef(0);
  const txLoadingRef = useRef(false);
  const overlayMouseDownRef = useRef(false);

  useEffect(() => {
    if (!naverDanjiCode) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [naverDanjiCode, onClose]);

  useEffect(() => {
    if (!naverDanjiCode) return;

    let alive = true;
    const load = async () => {
      setLoading(true);
      setError('');
      setDetail(null);
      setTxLoaded(false);
      setTransactions([]);
      setTxMonthlyStats([]);
      setTxChartItems([]);
      setTxMarketItems([]);
      setTxError('');
      setTxPtpNo('');
      setSelectedPyeong('');
      setTxPageNumber(1);
      setTxHasMoreRows(false);
      setTxRowsLoadingMore(false);
      setTxVisibleRowCount(TRANSACTION_PAGE_SIZE);
      setWeeklyRows([]);
      setWeeklyLoading(false);
      setFixArticles([]);
      setFixArticlesByType({ SALE: [], LEASE: [] });
      setFixArticleTradeType('SALE');
      setFixArticlesLoading(false);
      setFixArticlesError('');
      setFixArticlesExpanded(false);
      setAreaUnitMode('pyeong');
      setAreaExpanded(false);
      setTxDealViewMode('sale');
      setTxPeriodYears(null);

      try {
        const response = await getRankerDanjiDetail(naverDanjiCode);
        if (!alive) return;
        setDetail(response);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || '단지 상세 정보를 조회하는 중 오류가 발생했습니다.');
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [naverDanjiCode]);

  const loadWeeklyRows = useCallback(async ({ periodYears = null, fromDate = null, toDate = null } = {}) => {
    if (!naverDanjiCode) return;
    const seq = weeklyRequestSeqRef.current + 1;
    weeklyRequestSeqRef.current = seq;
    setWeeklyLoading(true);
    try {
      const response = await getWeeklyListingVolume(naverDanjiCode, {
        periodYears,
        fromDate,
        toDate,
        ptpNo: txPtpNo || null,
      });
      if (weeklyRequestSeqRef.current !== seq) return;
      const nextRows = Array.isArray(response?.items)
        ? response.items
        : (Array.isArray(response?.Items) ? response.Items : []);
      setWeeklyRows(nextRows);
    } catch {
      if (weeklyRequestSeqRef.current !== seq) return;
      setWeeklyRows([]);
    } finally {
      if (weeklyRequestSeqRef.current === seq) setWeeklyLoading(false);
    }
  }, [naverDanjiCode, txPtpNo]);

  const loadTransactions = useCallback(async (targetPage = 1, appendRows = false) => {
    if (!naverDanjiCode || txLoadingRef.current) return;

    txLoadingRef.current = true;
    if (appendRows) {
      setTxRowsLoadingMore(true);
    } else {
      setTxLoading(true);
    }
    setTxError('');
    try {
      const requestParams = {
        pageNumber: targetPage,
        pageSize: TRANSACTION_PAGE_SIZE,
        periodYears: txPeriodYears > 0 ? txPeriodYears : null,
        ptpNo: txPtpNo || null,
        dealType: txDealViewMode === 'sale' ? 1 : txDealViewMode === 'lease' ? 2 : null,
      };

      let response;
      try {
        response = await getRealTransactions(naverDanjiCode, requestParams);
      } catch {
        response = await getRealTransactionsMarket(naverDanjiCode, requestParams);
      }

      const nextItems = Array.isArray(response?.items) ? response.items : [];
      const totalCount = Number(response?.totalCount ?? response?.total ?? 0);
      const nextPageNumber = Number(response?.pageNumber || targetPage);
      const nextPageSize = Number(response?.pageSize || TRANSACTION_PAGE_SIZE);
      const loadedCount = appendRows ? (targetPage * nextPageSize) : nextItems.length;
      const hasMore = Number.isFinite(totalCount) && totalCount > 0
        ? loadedCount < totalCount
        : nextItems.length >= nextPageSize;

      setTransactions((prev) => (appendRows ? [...prev, ...nextItems] : nextItems));
      if (!appendRows) {
        setTxMonthlyStats(Array.isArray(response?.monthlyStats) ? response.monthlyStats : []);
        setTxChartItems(Array.isArray(response?.chartItems) ? response.chartItems : []);
        try {
          setTxMarketLoading(true);
          const askPriceResponse = await getAskPriceVolumeChart(naverDanjiCode, {
            ptpNo: txPtpNo || null,
            dealType: askPriceViewMode === 'sale' ? 1 : askPriceViewMode === 'lease' ? 2 : null
          });
          setTxMarketItems(Array.isArray(askPriceResponse?.items) ? askPriceResponse.items : []);
          setTxTargetPrice(askPriceResponse?.targetPrice || null);
        } catch {
          setTxMarketItems([]);
          setTxTargetPrice(null);
        } finally {
          setTxMarketLoading(false);
        }
        setTxVisibleRowCount(TRANSACTION_PAGE_SIZE);
      } else {
        setTxVisibleRowCount((prev) => prev + TRANSACTION_PAGE_SIZE);
      }
      setTxPageNumber(nextPageNumber);
      setTxHasMoreRows(hasMore);
      setTxLoaded(true);
    } catch (e) {
      setTxError(e?.message || '실거래 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      txLoadingRef.current = false;
      setTxLoading(false);
      setTxRowsLoadingMore(false);
    }
  }, [naverDanjiCode, txDealViewMode, txPeriodYears, txPtpNo, askPriceViewMode]);

  useEffect(() => {
    if (!detail) return;
    if (!txPtpNo) return;
    loadTransactions(1, false);
  }, [detail, loadTransactions, txPtpNo, txDealViewMode, askPriceViewMode]);

  useEffect(() => {
    if (!detail) return;
    if (!txPtpNo) return;
    loadWeeklyRows({ periodYears: 5, fromDate: null, toDate: null });
  }, [detail, loadWeeklyRows, txPtpNo]);

  const loadMoreTransactions = useCallback(() => {
    if (!txHasMoreRows || txRowsLoadingMore || txLoadingRef.current) return;
    const nextPage = txPageNumber + 1;
    loadTransactions(nextPage, true);
  }, [loadTransactions, txHasMoreRows, txRowsLoadingMore, txPageNumber]);

  // 실거래/호가 등 하단 차트들의 매매·전세·전세가율 토글을 하나로 동기화.
  // 호가 차트에는 전세가율 토글이 없으므로 'ratio'는 전세(lease)로 매핑해 표시.
  const handleSharedViewModeChange = useCallback((mode) => {
    setTxDealViewMode(mode);
    setAskPriceViewMode(mode === 'ratio' ? 'lease' : mode);
  }, []);

  const areaOptions = useMemo(() => (
    Array.isArray(detail?.areaOptions) ? detail.areaOptions : []
  ), [detail]);

  const pyeongGroups = useMemo(() => {
    if (!Array.isArray(areaOptions)) return new Map();
    const groups = new Map();
    areaOptions.forEach((option) => {
      const pyeong = extractPyeongValue(option);
      if (pyeong === null) return;
      if (!groups.has(pyeong)) {
        groups.set(pyeong, []);
      }
      groups.get(pyeong).push(option);
    });
    return groups;
  }, [areaOptions]);

  const allowedPtpNoSet = useMemo(() => {
    const raw = String(detail?.fixPtpNoCsv ?? summary?.ptpNoCsv ?? detail?.ptpNoCsv ?? '').trim();
    if (!raw) return null;
    const values = raw
      .split(',')
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    return values.length > 0 ? new Set(values) : null;
  }, [detail?.fixPtpNoCsv, summary?.ptpNoCsv, detail?.ptpNoCsv]);

  const pyeongButtons = useMemo(() => {
    const buttons = [];
    pyeongGroups.forEach((options, pyeong) => {
      let label;
      if (areaUnitMode === 'm2') {
        const firstOption = options[0];
        const splySpc = Number(firstOption?.splySpc ?? firstOption?.sply_spc ?? 0);
        if (Number.isFinite(splySpc) && splySpc > 0) {
          label = `${splySpc % 1 === 0 ? splySpc.toFixed(0) : splySpc.toFixed(2)}㎡`;
        } else {
          label = `${pyeong}평`;
        }
      } else {
        label = `${pyeong}평`;
      }

      const hasAllowedPtp = !allowedPtpNoSet || options.some(option => {
        const ptpNo = String(option?.ptpNo ?? option?.value ?? '').trim();
        return allowedPtpNoSet.has(ptpNo);
      });

      buttons.push({
        key: `pyeong-${pyeong}`,
        pyeong,
        label,
        options,
        disabled: !hasAllowedPtp,
      });
    });
    return buttons.sort((a, b) => a.pyeong - b.pyeong);
  }, [pyeongGroups, areaUnitMode, allowedPtpNoSet]);

  const detailAreaButtons = useMemo(() => {
    if (!Array.isArray(areaOptions)) return [];
    return areaOptions.map((option, index) => {
      const value = String(option?.ptpNo ?? option?.value ?? '').trim();
      return {
        key: value || `area-${index}`,
        value,
        label: formatAreaOptionLabel({ ...option, __areaUnit: areaUnitMode }),
        familyCnt: option?.familyCnt ?? option?.FamilyCnt ?? option?.family_cnt ?? null,
        pyeong: extractPyeongValue(option),
      };
    }).filter((option, index, arr) => option.label && arr.findIndex((x) => x.label === option.label) === index);
  }, [areaOptions, areaUnitMode]);

  const selectedPyeongOptions = useMemo(() => {
    if (!selectedPyeong) return [];
    return pyeongGroups.get(selectedPyeong) || [];
  }, [selectedPyeong, pyeongGroups]);

  const inlinePyeongButtons = useMemo(() => pyeongButtons.slice(0, 4), [pyeongButtons]);
  const hasMorePyeongButtons = pyeongButtons.length > inlinePyeongButtons.length;
  // 우측 단지정보의 '평형/타입' 은 단지의 모든 평형/타입을 표시하되,
  // 선택한 평형에 속한 타입만 활성(진하게), 나머지는 연하게 구분. 활성 타입을 앞으로 정렬.
  const areaTypeText = useMemo(() => (
    detailAreaButtons.map((option) => option.label).filter(Boolean).join(', ')
  ), [detailAreaButtons]);
  const areaTypeList = useMemo(() => {
    const list = detailAreaButtons
      .filter((b) => b.label)
      .map((b) => ({ label: b.label, active: selectedPyeong ? b.pyeong === selectedPyeong : true }));
    // 활성(선택 평형) 타입을 앞으로 (stable)
    return list.slice().sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1));
  }, [detailAreaButtons, selectedPyeong]);

  // ptp_no_csv 가 콤마로 여러 개면 첫 번째 ptp 기준으로 평형 속성 조회
  const primaryPtpNo = useMemo(() => String(txPtpNo || '').split(',')[0].trim(), [txPtpNo]);
  const selectedAreaFamilyCnt = useMemo(() => {
    const selected = primaryPtpNo;
    if (!selected) return null;
    const selectedNorm = normalizePtpToken(selected);

    const foundFromButtons = detailAreaButtons.find((option) => {
      const value = String(option?.value || '').trim();
      const valueNorm = normalizePtpToken(value);
      return value === selected || (selectedNorm && valueNorm && valueNorm === selectedNorm);
    });
    if (foundFromButtons) {
      const btnNum = Number(foundFromButtons.familyCnt);
      if (Number.isFinite(btnNum)) return btnNum;
    }

    const found = areaOptions.find((option) => {
      const value = String(option?.ptpNo ?? option?.value ?? '').trim();
      const valueNorm = normalizePtpToken(value);
      return value === selected || (selectedNorm && valueNorm && valueNorm === selectedNorm);
    });
    if (!found) return null;
    const num = Number(found?.familyCnt ?? found?.FamilyCnt ?? found?.family_cnt);
    return Number.isFinite(num) ? num : null;
  }, [areaOptions, detailAreaButtons, primaryPtpNo]);
  const selectedAreaOption = useMemo(() => {
    const selected = primaryPtpNo;
    if (!selected) return null;
    const selectedNorm = normalizePtpToken(selected);
    return areaOptions.find((option) => {
      const value = String(option?.ptpNo ?? option?.value ?? '').trim();
      const valueNorm = normalizePtpToken(value);
      return value === selected || (selectedNorm && valueNorm && valueNorm === selectedNorm);
    }) || null;
  }, [areaOptions, primaryPtpNo]);
  const selectedAreaManagementCost = useMemo(() => {
    if (!selectedAreaOption) return null;
    const num = Number(
      selectedAreaOption?.managementTotalPrice
      ?? selectedAreaOption?.ManagementTotalPrice
      ?? selectedAreaOption?.management_total_price
    );
    return Number.isFinite(num) ? num : null;
  }, [selectedAreaOption]);
  const selectedAreaHoldingTax = useMemo(() => {
    if (!selectedAreaOption) return null;
    const direct = Number(
      selectedAreaOption?.holdingTotalTax
      ?? selectedAreaOption?.HoldingTotalTax
      ?? selectedAreaOption?.holding_total_tax
    );
    if (Number.isFinite(direct)) return direct;
    const property = Number(
      selectedAreaOption?.propertyTotalTax
      ?? selectedAreaOption?.PropertyTotalTax
      ?? selectedAreaOption?.property_total_tax
    );
    const realEstate = Number(
      selectedAreaOption?.realEstateTotalTax
      ?? selectedAreaOption?.RealEstateTotalTax
      ?? selectedAreaOption?.real_estate_total_tax
    );
    if (Number.isFinite(property) || Number.isFinite(realEstate)) {
      return (Number.isFinite(property) ? property : 0) + (Number.isFinite(realEstate) ? realEstate : 0);
    }
    return null;
  }, [selectedAreaOption]);

  useEffect(() => {
    if (selectedPyeong) return;
    if (!pyeongButtons.length) return;

    const rowFirstPtp = String(summary?.ptpNo ?? summary?.ptp_no ?? '').split(',')[0].trim();
    let target = null;
    if (rowFirstPtp) {
      target = pyeongButtons.find((button) => !button.disabled
        && (button.options || []).some((o) => String(o?.ptpNo ?? o?.value ?? '').trim() === rowFirstPtp));
    }
    if (!target) target = pyeongButtons.find((button) => !button.disabled);
    if (target?.pyeong) setSelectedPyeong(target.pyeong);
  }, [pyeongButtons, selectedPyeong, summary]);

  useEffect(() => {
    if (!selectedPyeongOptions.length) return;

    const ptpNos = selectedPyeongOptions
      .map((option) => String(option?.ptpNo ?? option?.value ?? '').trim())
      .filter(Boolean);

    if (ptpNos.length > 0) {
      setTxPtpNo(ptpNos.join(','));
    }
  }, [selectedPyeongOptions]);

  useEffect(() => {
    const ptp = String(txPtpNo || '').trim();
    if (!naverDanjiCode || !ptp) {
      setFixArticles([]);
      setFixArticlesByType({ SALE: [], LEASE: [] });
      setFixArticlesError('');
      setFixArticlesLoading(false);
      return undefined;
    }

    let alive = true;
    const normalizeRows = (rows, tradeType) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((row) => {
        const articleNo = String(row?.articleNo ?? row?.article_no ?? '').trim();
        const parts = String(row?.articleText ?? row?.article_text ?? '')
          .split('/')
          .map((v) => String(v || '').trim());
        const areaName = String(row?.areaName ?? row?.area_name ?? '').trim();
        const dongNm = String(row?.dongNm ?? row?.dong_nm ?? '').trim();
        return {
          type: areaName || '-',
          dong: dongNm || '-',
          articleNo,
          price: formatFixArticlePrice(parts[0]),
          floor: parts[2] || '-',
          totalFloor: parts[3] || '-',
          direction: parts[4] || '-',
        };
      });
    };

    const loadFixArticles = async () => {
      setFixArticlesLoading(true);
      setFixArticlesError('');
      try {
        const response = await getRankerFixArticles({ naverDanjiCode, ptpNo: ptp });
        if (!alive) return;
        const saleRows = normalizeRows(response?.saleItems ?? response?.sale_items, 'SALE');
        const leaseRows = normalizeRows(response?.leaseItems ?? response?.lease_items, 'LEASE');
        setFixArticlesByType({ SALE: saleRows, LEASE: leaseRows });
        setFixArticleTradeType('SALE');
        setFixArticles([...saleRows, ...leaseRows]);
      } catch (e) {
        if (!alive) return;
        setFixArticles([]);
        setFixArticlesByType({ SALE: [], LEASE: [] });
        setFixArticlesError(e?.message || '추천매물 정보를 불러오지 못했습니다.');
      } finally {
        if (alive) setFixArticlesLoading(false);
      }
    };

    loadFixArticles();
    return () => {
      alive = false;
    };
  }, [naverDanjiCode, txPtpNo]);

  const saleFixArticles = useMemo(() => {
    return (fixArticlesByType.SALE || []).slice(0, 5).map(item => ({ ...item, tradeType: '매매' }));
  }, [fixArticlesByType]);

  const leaseFixArticles = useMemo(() => {
    return (fixArticlesByType.LEASE || []).slice(0, 5).map(item => ({ ...item, tradeType: '전세' }));
  }, [fixArticlesByType]);

  const translateDirection = (direction) => {
    const directionMap = {
      'N': '북', 'NN': '북', 'NE': '북동', 'EN': '북동', 'E': '동', 'EE': '동',
      'SE': '남동', 'ES': '남동', 'S': '남', 'SS': '남', 'SW': '남서', 'WS': '남서',
      'W': '서', 'WW': '서', 'NW': '북서', 'WN': '북서',
    };
    const dir = String(direction || '').trim().toUpperCase();
    return directionMap[dir] || direction || '-';
  };

  if (!naverDanjiCode) return null;

  return (
    <div
      className="ranking-detail-modal-overlay"
      onMouseDown={(event) => {
        overlayMouseDownRef.current = event.target === event.currentTarget;
      }}
      onMouseUp={(event) => {
        const isOverlayMouseUp = event.target === event.currentTarget;
        if (overlayMouseDownRef.current && isOverlayMouseUp) onClose?.();
        overlayMouseDownRef.current = false;
      }}
    >
      <div className="ranking-detail-modal ranking-detail-modal-v2" onClick={(event) => event.stopPropagation()}>
        <header className="ranking-detail-modal-header">
          <div className="ranking-detail-modal-title-wrap">
            <div className="ranking-detail-modal-top-row">
              <div className="ranking-detail-modal-title-row">
                <h2>{detail?.buildingName || '단지 상세'}</h2>
                <div className="grade-badges">
                  <span className="grade-badge-wrap sigungu">
                    <span className="grade-badge-label">지역급지</span>
                    <span className="sigungu-grade-badge">{formatGradeBadgeText(detail?.sigunguGrade)}</span>
                  </span>
                  <span className="grade-badge-wrap dong">
                    <span className="grade-badge-label">생활권급지</span>
                    <span className="dong-grade-badge">{formatGradeBadgeText(detail?.dongGrade)}</span>
                  </span>
                </div>
                <div className="ranking-detail-quick-actions" aria-label="단지 바로가기 기능">
                  <button
                    type="button"
                    className="ranking-detail-icon-btn"
                    title="즐겨찾기"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOpenFavoriteGroupPicker) {
                        const itemData = { naverDanjiCode, ...summary, ...detail };
                        onOpenFavoriteGroupPicker(itemData);
                      }
                    }}
                  >
                    {isFavorite ? '♥' : '♡'}
                  </button>
                  <button type="button" className="ranking-detail-icon-btn" title="공유">
                    <img src="/assets/share.png" alt="" aria-hidden="true" className="ranking-detail-share-icon" />
                  </button>
                  <button
                    type="button"
                    className="ranking-detail-icon-btn ranking-detail-icon-btn-naver"
                    onClick={() => window.open(`https://new.land.naver.com/complexes/${naverDanjiCode}`, '_blank', 'noopener,noreferrer')}
                    title="네이버에서 보기"
                    aria-label="네이버에서 보기"
                  >
                    N
                  </button>
                </div>
              </div>
            </div>
            <div className="ranking-detail-modal-meta-row">
              <p>{formatHeaderMeta(detail, summary)}</p>
            </div>

            {/* 평형 선택 및 추천매물 통합 행 */}
            <div className="ranking-detail-meta-bottom-row">
              {/* 좌측: 평형 선택 */}
              <div className="ranking-detail-area-picker-inline">
                <div className="ranking-detail-area-chip-row inline-meta">
                <button
                  type="button"
                  className="ranking-detail-icon-btn ranking-detail-ptp-btn"
                  title="단지 평형"
                  onClick={() => setAreaUnitMode((prev) => (prev === 'm2' ? 'pyeong' : 'm2'))}
                >
                  <span className="ranking-detail-ptp-refresh" aria-hidden="true">↻</span>
                  <span>{areaUnitMode === 'm2' ? '㎡' : '평'}</span>
                </button>
                {inlinePyeongButtons.map((button) => {
                  const active = button.pyeong === selectedPyeong;
                  const disabled = button.disabled;
                  return (
                    <button
                      key={button.key}
                      type="button"
                      className={`ranking-detail-area-chip ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                      onClick={() => {
                        if (disabled) return;
                        setSelectedPyeong(active ? '' : button.pyeong);
                      }}
                      disabled={disabled}
                    >
                      {button.label}
                    </button>
                  );
                })}
                {hasMorePyeongButtons && (
                  <button
                    type="button"
                    className={`ranking-detail-icon-btn ranking-detail-more-btn ${areaExpanded ? 'open' : ''}`}
                    title="평형 더보기"
                    onClick={() => setAreaExpanded((prev) => !prev)}
                    aria-label="평형 더보기"
                  >
                    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                      {areaExpanded ? (
                        <path d="M5 12 L10 7 L15 12" />
                      ) : (
                        <path d="M5 8 L10 13 L15 8" />
                      )}
                    </svg>
                  </button>
                )}
                </div>
                {areaExpanded && hasMorePyeongButtons && (
                  <div className="ranking-detail-area-popover">
                    {pyeongButtons.map((button) => {
                      const active = button.pyeong === selectedPyeong;
                      const disabled = button.disabled;
                      return (
                        <button
                          key={`expand-${button.key}`}
                          type="button"
                          className={`ranking-detail-area-chip ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                          onClick={() => {
                            if (disabled) return;
                            setSelectedPyeong(active ? '' : button.pyeong);
                          }}
                          disabled={disabled}
                        >
                          {button.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 우측: 추천매물 */}
              {!fixArticlesLoading && !fixArticlesError && (saleFixArticles.length > 0 || leaseFixArticles.length > 0) && (
                <div className="ranking-detail-fix-horizontal-wrapper">
                  {saleFixArticles.length > 0 && (
                    <div className="ranking-detail-fix-group">
                      <div className="ranking-detail-fix-group-title">매매 (추천매물)</div>
                      <div className="ranking-detail-fix-group-items-horizontal">
                        {saleFixArticles.map((item, index) => (
                          <a
                            key={`sale-${item.articleNo || 'article'}-${index}`}
                            href={item.articleNo ? `https://fin.land.naver.com/articles/${item.articleNo}` : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ranking-detail-fix-item"
                            title="클릭시 이동합니다"
                          >
                            <span className="fix-item-price">{item.price} ({item.type})</span>
                            <span className="fix-item-detail">{String(item.floor).replace(/층$/, '')}층 · {translateDirection(item.direction)}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {leaseFixArticles.length > 0 && (
                    <div className="ranking-detail-fix-group">
                      <div className="ranking-detail-fix-group-title">전세 (추천매물)</div>
                      <div className="ranking-detail-fix-group-items-horizontal">
                        {leaseFixArticles.map((item, index) => (
                          <a
                            key={`lease-${item.articleNo || 'article'}-${index}`}
                            href={item.articleNo ? `https://fin.land.naver.com/articles/${item.articleNo}` : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ranking-detail-fix-item"
                            title="클릭시 이동합니다"
                          >
                            <span className="fix-item-price">{item.price} ({item.type})</span>
                            <span className="fix-item-detail">{String(item.floor).replace(/층$/, '')}층 · {translateDirection(item.direction)}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="ranking-detail-modal-actions">
            <button type="button" className="ranking-detail-modal-close" onClick={onClose} aria-label="닫기">
              ×
            </button>
          </div>
        </header>

        <section className="ranking-detail-modal-body rdv2-body">
          {loading && <div className="ranking-detail-modal-status">상세 정보를 불러오는 중입니다...</div>}
          {!loading && error && <div className="ranking-detail-modal-status error">{error}</div>}

          {!loading && !error && detail && (
            <div className="rdv2-stack">
              {/* 상단: 단지정보 (전체폭) */}
              <div className="rdv2-info-top">
                <RankingDetailComplexInfoTable
                  detail={detail}
                  summary={summary}
                  areaTypeText={areaTypeText}
                  areaTypeList={areaTypeList}
                  selectedAreaFamilyCnt={selectedAreaFamilyCnt}
                  selectedAreaManagementCost={selectedAreaManagementCost}
                  selectedAreaHoldingTax={selectedAreaHoldingTax}
                  formatUseApproveDate={formatUseApproveDate}
                  formatNumber={fmtNumber}
                  formatValue={fmt}
                  formatHeat={fmtHeat}
                />
              </div>

              {/* 하단: 실거래/차트 (각 섹션 차트|표 수평) */}
              <div className="rdv2-charts">
                <div className="ranking-detail-market-wrap">
                  {!txLoaded && txLoading && (
                    <div className="ranking-detail-modal-status">실거래 데이터를 불러오는 중입니다...</div>
                  )}
                  <RealTransactionNewCard
                    transactions={transactions}
                    chartItems={txChartItems}
                    marketItems={txMarketItems}
                    targetPrice={txTargetPrice}
                    monthlyStats={txMonthlyStats}
                    ptpNo={txPtpNo}
                    ptpOptions={selectedPyeongOptions}
                    selectedPyeongOptions={selectedPyeongOptions}
                    onPtpChange={setTxPtpNo}
                    viewMode={txDealViewMode}
                    onViewModeChange={handleSharedViewModeChange}
                    askPriceViewMode={askPriceViewMode}
                    onAskPriceViewModeChange={handleSharedViewModeChange}
                    loading={txLoading}
                    error={txError}
                    hasMoreRows={txHasMoreRows}
                    rowsLoadingMore={txRowsLoadingMore}
                    onLoadMoreRows={loadMoreTransactions}
                    visibleRowCount={txVisibleRowCount}
                    weeklyRows={weeklyRows}
                    weeklyLoading={weeklyLoading}
                    marketLoading={txMarketLoading}
                    onWeeklyRangeChange={({ periodYears, fromDate, toDate }) => loadWeeklyRows({ periodYears, fromDate, toDate })}
                    familyCount={detail?.familyCnt ?? summary?.familyCnt ?? null}
                  />
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default RankingComplexDetailModalV2;
